import {Box} from '@mui/material';
import {
  MOON_100,
  MOON_200,
  MOON_800,
  OBLIVION,
} from '@wandb/weave/common/css/color.styles';
import {hexToRGB} from '@wandb/weave/common/css/utils';
import {useOrgName} from '@wandb/weave/common/hooks/useOrganization';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {
  getHostedModels,
  MODEL_INDEX,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/inference/modelInfo';
import {ModelTile} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/inference/ModelTile';
import {Pill} from '@wandb/weave/components/Tag';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import {urlPrefixed} from '@wandb/weave/config';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {components, OptionProps} from 'react-select';

import {LlmStructuredCompletionModel} from '../../../wfReactInterface/generatedBuiltinObjectClasses.zod';
import {TraceObjSchemaForBaseObjectClass} from '../../../wfReactInterface/objectClassQuery';
import {
  findMaxTokensByModelName,
  LLM_MAX_TOKENS,
  LLM_PROVIDER_LABELS,
  LLMMaxTokensKey,
} from '../../llmMaxTokens';
import {
  OptionalSavedPlaygroundModelParams,
  SavedPlaygroundModelState,
} from '../../types';
import {ProviderStatus} from '../../useConfiguredProviders';
import {convertDefaultParamsToOptionalPlaygroundModelParams} from '../../useSaveModelConfiguration';
import {
  OpenDirection,
  Position,
  SubMenu,
  SubMenuItem,
} from '../dropdownComponents';

export const SAVED_MODEL_OPTION_VALUE = 'saved-models';

export interface LLMOption {
  catalogId?: string; // Model Catalog ID
  label: string;
  subLabel?: string | React.ReactNode;
  isNew?: boolean;
  is_image_model?: boolean;
  value: LLMMaxTokensKey | string;
  max_tokens: number;
  max_tokens_default: number;

  // Saved LLM options
  baseModelId?: LLMMaxTokensKey | null;
  defaultParams?: OptionalSavedPlaygroundModelParams;
  versionIndex?: number | null;
  isLatest?: boolean;
  objectId?: string;
}

export interface ProviderOption {
  label: string | React.ReactNode;
  value: string;
  llms: Array<LLMOption>;
  isDisabled?: boolean;
  providers?: ProviderOption[];
  isPreview?: boolean; // If true, show a preview pill in the dropdown
  isActionItem?: boolean; // If true, this is an action item (e.g., configure providers) rather than an actual provider
  actionType?:
    | 'configure-provider-list'
    | 'activate-inference'
    | 'configure-provider';
}

export interface CustomOptionProps extends OptionProps<ProviderOption, false> {
  onChange: (
    value: LLMMaxTokensKey,
    maxTokens: number,
    maxTokensDefault: number,
    savedModel?: SavedPlaygroundModelState
  ) => void;
  entity: string;
  project: string;
  isAdmin?: boolean;
  onConfigureProvider?: (provider: string) => void;
  onViewCatalog?: (path?: string) => void;
  providers?: ProviderOption[];
  direction: OpenDirection;
}

const LLMSubMenu = ({
  value,
  llms,
  onChange,
  position,
  onSelect,
  isAdmin,
  onConfigureProvider,
  onViewCatalog,
  providers,
  direction,
  entity,
}: {
  value: string;
  llms: Array<LLMOption>;
  onChange: (
    value: LLMMaxTokensKey,
    maxTokens: number,
    maxTokensDefault: number,
    savedModel?: SavedPlaygroundModelState
  ) => void;
  position: Position;
  onSelect: () => void;
  isAdmin?: boolean;
  onConfigureProvider?: (provider: string) => void;
  onViewCatalog?: (path?: string) => void;
  providers?: ProviderOption[];
  direction: OpenDirection;
  entity?: string;
}) => {
  return (
    <SubMenu position={position} direction={direction}>
      {llms.map(llm => {
        // TODO: Would be nice to have all models in the catalog so we could
        //       display tiles, link to comparison and details, etc.
        const isCoreWeaveModel = value === 'coreweave';
        const catalogModel = isCoreWeaveModel
          ? MODEL_INDEX[llm.catalogId ?? '']
          : null;
        const isUnspportedCoreWeaveModel =
          isCoreWeaveModel &&
          (!catalogModel || catalogModel.apiStyle !== 'chat');
        if (isUnspportedCoreWeaveModel) {
          // Playground doesn't support embedding models.
          return null;
        }
        return (
          <div key={`${llm.value}${llm.versionIndex}`} className="group">
            <SubMenuItem
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onChange(
                  llm.value as LLMMaxTokensKey,
                  llm.max_tokens,
                  llm.max_tokens_default,
                  llm.defaultParams && llm.baseModelId
                    ? LLMOptionToSavedPlaygroundModelState(llm)
                    : undefined
                );
                onSelect();
              }}>
              <div className="flex flex-1 items-center break-all">
                <span key={llm.value + 'label'}>{llm.label}</span>
                {llm.subLabel && (
                  <div
                    key={llm.value + 'sub-label'}
                    className="text-sm text-moon-500 pl-4">
                    {llm.subLabel}
                  </div>
                )}
                {llm.isNew && (
                  <Pill
                    key={llm.value + 'new-pill'}
                    className="ml-8 whitespace-nowrap"
                    label="New"
                    color="teal"
                  />
                )}
                {llm.is_image_model && (
                  <Pill
                    key={llm.value + 'image-pill'}
                    className="ml-8 whitespace-nowrap"
                    label="Image"
                    color="teal"
                  />
                )}
              </div>
              {catalogModel && onViewCatalog && (
                <div className="opacity-0 group-hover:opacity-100">
                  <Button
                    icon="info"
                    variant="ghost"
                    size="small"
                    tooltip={
                      <ModelTile
                        model={catalogModel}
                        hint="Click info button for full model details"
                      />
                    }
                    tooltipProps={{
                      className: 'p-0 bg-transparent',
                      side: 'bottom',
                      sideOffset: 8,
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      onViewCatalog(
                        `${catalogModel.provider}/${catalogModel.id}`
                      );
                    }}
                  />
                </div>
              )}
            </SubMenuItem>
          </div>
        );
      })}
      {onViewCatalog && !providers && value === 'coreweave' && (
        <div className="group">
          <SubMenuItem
            onClick={e => {
              e.stopPropagation();
              // TODO: Pass the value as the path if we add comparison for other providers
              onViewCatalog();
            }}>
            <div className="flex-1 font-semibold">Compare models</div>
            <div className="opacity-0 group-hover:opacity-100">
              <Icon width={18} height={18} name="forward-next" />
            </div>
          </SubMenuItem>
        </div>
      )}
      {providers?.map(provider => {
        const tooltipContent =
          provider.value !== 'custom-provider' && !isAdmin
            ? 'You must be an admin to configure this provider'
            : undefined;

        const trigger = (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: '6px',
              cursor: 'pointer',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: hexToRGB(OBLIVION, 0.04),
              },
              width: '100%',
            }}
            onClick={() => {
              if (provider.value === 'custom-provider' || isAdmin) {
                onConfigureProvider?.(provider.value);
              }
            }}>
            <Box
              sx={{
                wordBreak: 'break-all',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
              }}>
              {provider.label}
            </Box>
            <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
              <Button
                variant="ghost"
                size="small"
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onConfigureProvider?.(provider.value);
                }}
                disabled={!isAdmin && provider.value !== 'custom-provider'}>
                Configure
              </Button>
            </Box>
          </Box>
        );

        return tooltipContent ? (
          <Tooltip
            key={provider.value}
            content={tooltipContent}
            trigger={trigger}
          />
        ) : (
          trigger
        );
      })}
    </SubMenu>
  );
};

const SubMenuOption = ({
  children,
  onChange,
  entity,
  project,
  isAdmin,
  onConfigureProvider,
  onViewCatalog,
  direction,
  ...props
}: CustomOptionProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  });
  const optionRef = useRef<HTMLDivElement>(null);
  const {llms, isDisabled} = props.data;
  const hasOptions = llms.length > 0 || (props.data.providers?.length ?? 0) > 0;

  useEffect(() => {
    if (isHovered && optionRef.current) {
      const rect = optionRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        right: rect.right,
      });
    }
  }, [isHovered]);

  if (props.data.value === 'divider') {
    return (
      <Box
        sx={{
          borderBottom: `1px solid ${MOON_200}`,
          my: 1,
        }}
      />
    );
  }

  return (
    <Box
      ref={optionRef}
      onMouseEnter={() => !isDisabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: 'relative',
        width: '100%',
        opacity: isDisabled ? 0.5 : 1,
      }}>
      <Box sx={{position: 'relative', zIndex: 1}}>
        <components.Option {...props}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}>
            {direction.horizontal === 'left' && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  marginRight: '8px',
                  alignItems: 'center',
                }}>
                {hasOptions && (
                  <Icon
                    className="w-16 h-16"
                    name="chevron-back"
                    color="moon_500"
                  />
                )}
              </Box>
            )}
            <Box
              sx={{
                wordBreak: 'break-all',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                width: '90%',
              }}>
              <div className="flex items-center gap-8">
                {props.data.isActionItem &&
                  props.data.actionType === 'configure-provider' && (
                    <Icon className="w-16 h-16" name="add-new" />
                  )}
                {children}
                {props.data.isPreview && <Pill label="Preview" color="moon" />}
              </div>
            </Box>
            {direction.horizontal === 'right' && (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  marginLeft: 'auto',
                  alignItems: 'center',
                }}>
                {hasOptions && (
                  <Icon
                    className="w-16 h-16"
                    name="chevron-next"
                    color="moon_500"
                  />
                )}
              </Box>
            )}
          </Box>
        </components.Option>
      </Box>
      {isHovered && hasOptions && (
        <LLMSubMenu
          value={props.data.value}
          providers={props.data.providers}
          llms={llms}
          onChange={onChange}
          position={position}
          onSelect={() => {
            props.selectProps.onInputChange?.('', {
              action: 'set-value',
              prevInputValue: props.selectProps.inputValue,
            });
            props.selectProps.onMenuClose?.();
          }}
          isAdmin={isAdmin}
          onConfigureProvider={onConfigureProvider}
          onViewCatalog={onViewCatalog}
          direction={direction}
          entity={entity}
        />
      )}
    </Box>
  );
};

export const CustomOption = ({
  children,
  onChange,
  entity,
  project,
  isAdmin,
  onConfigureProvider,
  onViewCatalog,
  data,
  ...props
}: CustomOptionProps) => {
  const {orgName} = useOrgName({entityName: entity});
  const {inputValue} = props.selectProps;
  const history = useHistory();

  // Handle action items (activate inference, configure providers)
  if (data.isActionItem && data.actionType === 'activate-inference') {
    return (
      <div className="relative w-full">
        <components.Option {...props} data={data}>
          <TargetBlank
            href={urlPrefixed(`/account-settings/${orgName}/billing`)}
            onClick={() => {
              props.selectProps.onInputChange?.('', {
                action: 'set-value',
                prevInputValue: props.selectProps.inputValue,
              });
              props.selectProps.onMenuClose?.();
            }}
            className="flex items-center font-normal gap-8 cursor-pointer w-full no-underline hover:text-moon-800 dark:hover:text-moon-100">
            <Icon className="w-16 h-16" name="inference" />
            <span>{children}</span>
          </TargetBlank>
        </components.Option>
      </div>
    );
  }

  if (data.isActionItem && data.actionType === 'configure-provider-list') {
    return (
      <div className="relative w-full">
        <components.Option {...props} data={data}>
          <div
            onClick={() => {
              history.push(`/${entity}/${project}/overview/providers`);
              props.selectProps.onInputChange?.('', {
                action: 'set-value',
                prevInputValue: props.selectProps.inputValue,
              });
              props.selectProps.onMenuClose?.();
            }}
            className="flex items-center font-normal gap-8 cursor-pointer w-full">
            <Icon className="w-16 h-16" name="settings" />
            <span>{children}</span>
          </div>
        </components.Option>
      </div>
    );
  }

  // If searching, show nested structure
  if (inputValue) {
    const isDisabled = data.isDisabled;
    if (isDisabled) {
      return null;
    }

    const filteredLLMs = data.llms
      .filter(
        llm =>
          llm.value.toLowerCase().includes(inputValue.toLowerCase()) ||
          llm.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          (llm.subLabel &&
            llm.subLabel
              .toString()
              .toLowerCase()
              .includes(inputValue.toLowerCase())) ||
          (llm.is_image_model && 'image'.includes(inputValue.toLowerCase())) ||
          (llm.isNew && 'new'.includes(inputValue.toLowerCase())) ||
          (llm.isLatest && 'latest'.includes(inputValue.toLowerCase()))
      )
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });

    return (
      <Box>
        <Box
          sx={{
            padding: '4px 12px 0',
            color: MOON_800,
            fontWeight: 600,
            cursor: 'default',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            wordBreak: 'break-all',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
          }}>
          <span>{data.label}</span>
        </Box>
        <Box
          sx={{
            px: '4px',
            wordBreak: 'break-all',
            wordWrap: 'break-word',
            whiteSpace: 'normal',
          }}>
          {filteredLLMs.map(llm => (
            <Box
              key={`${llm.value}${llm.versionIndex}`}
              onClick={() => {
                onChange(
                  llm.value as LLMMaxTokensKey,
                  llm.max_tokens,
                  llm.max_tokens_default,
                  llm.defaultParams && llm.baseModelId
                    ? LLMOptionToSavedPlaygroundModelState(llm)
                    : undefined
                );
                props.selectProps.onInputChange?.('', {
                  action: 'set-value',
                  prevInputValue: props.selectProps.inputValue,
                });
                props.selectProps.onMenuClose?.();
              }}
              sx={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderRadius: '4px',
                '&:hover': {
                  backgroundColor: MOON_100,
                },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <div>
                <span key={llm.value + 'label'}>{llm.label}</span>
                {llm.subLabel && (
                  <div
                    key={llm.value + 'sub-label'}
                    className="text-sm text-moon-500">
                    {llm.subLabel}
                  </div>
                )}
              </div>
              {llm.isLatest && (
                <Pill
                  key={llm.value + 'latest-pill'}
                  label="Latest"
                  color="moon"
                />
              )}
              {llm.is_image_model && (
                <Pill
                  key={llm.value + 'image-pill'}
                  label="Image"
                  color="teal"
                />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  const filteredData = {
    ...data,
    llms: data.llms.filter(llm => !!llm.isLatest),
  };
  // If not searching, use the hover submenu
  return (
    <SubMenuOption
      {...props}
      data={data.value === SAVED_MODEL_OPTION_VALUE ? filteredData : data}
      onChange={onChange}
      entity={entity}
      project={project}
      isAdmin={isAdmin}
      onConfigureProvider={onConfigureProvider}
      onViewCatalog={onViewCatalog}>
      {children}
    </SubMenuOption>
  );
};

export const dividerOption: ProviderOption = {
  label: '',
  value: 'divider',
  llms: [],
};

export const addProviderOption = (
  disabledOptions: ProviderOption[]
): ProviderOption => ({
  label: 'Add AI provider',
  value: 'configure-provider',
  llms: [],
  isActionItem: true,
  actionType: 'configure-provider',
  providers: [
    ...disabledOptions,
    {
      label: 'Custom provider',
      value: 'custom-provider',
      llms: [],
    },
  ],
});

export const configureProvidersOption: ProviderOption = {
  label: 'Configure providers',
  value: 'configure-providers',
  llms: [],
  isActionItem: true,
  actionType: 'configure-provider-list',
};

export const activateInferenceOption = (entity: string): ProviderOption => ({
  label: 'Activate W&B Inference',
  value: 'activate-inference',
  llms: [],
  isActionItem: true,
  actionType: 'activate-inference',
});

export const useLLMDropdownOptions = (
  configuredProviders: Record<string, ProviderStatus>,
  configuredProvidersLoading: boolean,
  customProvidersResult: TraceObjSchemaForBaseObjectClass<'Provider'>[],
  customProviderModelsResult: TraceObjSchemaForBaseObjectClass<'ProviderModel'>[],
  customLoading: boolean,
  savedModelsResult: TraceObjSchemaForBaseObjectClass<'LLMStructuredCompletionModel'>[],
  savedModelsLoading: boolean,
  canAccessInference: boolean = true,
  entity?: string,
  canActivateInference: boolean = false
) => {
  const options: ProviderOption[] = [];
  const disabledOptions: ProviderOption[] = [];
  const savedModelsOptions: ProviderOption[] = [];

  if (configuredProvidersLoading) {
    options.push({
      label: 'Loading providers...',
      value: 'loading',
      llms: [],
    });
  } else {
    Object.entries(configuredProviders).forEach(([provider, {status}]) => {
      if (provider === 'coreweave' && !canAccessInference) {
        return;
      }
      const providerLLMs = [];
      if (provider === 'coreweave') {
        // Insert hosted models
        providerLLMs.push(
          ...getHostedModels().map(model => {
            const llMaxTokensInfo = LLM_MAX_TOKENS[model.id as LLMMaxTokensKey];
            const maxTokens = llMaxTokensInfo.max_tokens;
            const maxTokensDefault =
              llMaxTokensInfo.max_tokens_default ?? Math.floor(maxTokens / 2);
            return {
              catalogId: model.id,
              value: `coreweave/${model.idPlayground}`, // What gets sent to completion endpoint
              label: model.label ?? model.id, // What gets shown as selected
              max_tokens: maxTokens,
              max_tokens_default: maxTokensDefault,
              isNew: model.isNew,
            };
          })
        );
      } else {
        providerLLMs.push(
          ...Object.entries(LLM_MAX_TOKENS)
            .filter(([_, config]) => config.provider === provider)
            .map(([llmKey]) => {
              const llMaxTokensInfo = LLM_MAX_TOKENS[llmKey as LLMMaxTokensKey];
              const maxTokens = llMaxTokensInfo.max_tokens;
              const maxTokensDefault =
                llMaxTokensInfo.max_tokens_default ?? Math.floor(maxTokens / 2);
              return {
                label: llmKey,
                value: llmKey as LLMMaxTokensKey,
                max_tokens: maxTokens,
                max_tokens_default: maxTokensDefault,
                is_image_model: llMaxTokensInfo.is_image_model,
              };
            })
        );
      }

      const option = {
        label:
          LLM_PROVIDER_LABELS[provider as keyof typeof LLM_PROVIDER_LABELS],
        value: provider,
        isPreview: false, // Not removing this capability in case we introduce other preview providers.
        llms: status ? providerLLMs : [],
        isDisabled: !status,
      };

      if (!status) {
        disabledOptions.push(option);
      } else {
        options.push(option);
      }
    });
  }

  // Add custom providers
  if (!customLoading) {
    customProvidersResult?.forEach(provider => {
      const providerName = provider.val.name || '';
      const currentProviderModels =
        customProviderModelsResult?.filter(
          obj => obj.val.provider === provider.digest
        ) || [];

      const shortenedProviderLabel =
        providerName.length > 20
          ? providerName.slice(0, 2) + '...' + providerName.slice(-4)
          : providerName;

      const llmOptions = currentProviderModels.map(model => ({
        label: shortenedProviderLabel + '/' + model.val.name,
        // Use custom:: prefix with provider::model format (using actual names)
        // Format: custom::<provider>::<model>
        value: ('custom::' +
          provider.val.name +
          '::' +
          model.val.name) as LLMMaxTokensKey,
        max_tokens: model.val.max_tokens,
        max_tokens_default: Math.floor(model.val.max_tokens / 2),
      }));

      if (llmOptions.length > 0) {
        options.push({
          label: providerName,
          value: providerName,
          llms: llmOptions,
        });
      }
    });
  }

  const savedModels = useMemo(
    () =>
      // If savedModelsResult is undefined, return an empty array
      // Else convert the saved models to LLMOption(s)
      (savedModelsResult || []).map(savedModelObj => {
        const savedModelVal = savedModelObj.val as LlmStructuredCompletionModel;
        const baseModelId = savedModelVal.llm_model_id;
        const savedModelName =
          savedModelVal.name ?? savedModelObj.object_id ?? 'Unnamed Model';

        let maxTokens: number | undefined;
        let maxTokensDefault: number | undefined;

        // Try to determine max tokens from built-in model list
        if (baseModelId in LLM_MAX_TOKENS) {
          const baseModelConfig =
            LLM_MAX_TOKENS[baseModelId as LLMMaxTokensKey];
          maxTokens = baseModelConfig.max_tokens;
          maxTokensDefault = baseModelConfig.max_tokens_default;
        } else {
          // Try to determine from custom provider models
          const customModelMatch = customProviderModelsResult?.find(
            customModel => {
              const customProviderMatch = customProvidersResult?.find(
                p => p.digest === customModel.val.provider
              );
              return (
                `${customProviderMatch?.val.name}/${customModel.val.name}` ===
                baseModelId
              );
            }
          );
          if (customModelMatch) {
            maxTokens = customModelMatch.val.max_tokens;
          }
        }
        // Fallback max tokens determination
        if (maxTokens === undefined) {
          maxTokens = findMaxTokensByModelName(baseModelId);
        }
        if (maxTokensDefault === undefined) {
          maxTokensDefault = Math.floor(maxTokens / 2);
        }

        const savedModelLabel = `${savedModelName}:v${savedModelObj.version_index}`;
        return {
          label: savedModelLabel,
          value: savedModelLabel,
          subLabel: baseModelId,
          baseModelId: baseModelId as LLMMaxTokensKey,
          max_tokens: maxTokens,
          max_tokens_default: maxTokensDefault,
          defaultParams: convertDefaultParamsToOptionalPlaygroundModelParams(
            savedModelVal.default_params ?? {messages_template: []}
          ),
          objectId: savedModelName,
          versionIndex: savedModelObj.version_index ?? null,
          isLatest: !!savedModelObj.is_latest,
        };
      }),
    [savedModelsResult, customProviderModelsResult, customProvidersResult]
  );

  if (!savedModelsLoading && savedModels.length > 0) {
    savedModelsOptions.push({
      label: 'Saved Models',
      value: SAVED_MODEL_OPTION_VALUE,
      llms: savedModels,
    });
  }

  // Combine options
  const allOptions = [...options, ...savedModelsOptions];

  // Add divider before add provider / activate inference options (only if there are providers)
  if (allOptions.length > 0) {
    allOptions.push(dividerOption);
  }

  // Add activate inference option if user cannot access inference but can access billing and env supports inference
  if (!canAccessInference && canActivateInference && entity) {
    allOptions.push(activateInferenceOption(entity));
  }

  // Only add "Add provider" option if there are existing providers
  allOptions.push(addProviderOption(disabledOptions));

  // Add "Configure providers" option
  allOptions.push(configureProvidersOption);

  return allOptions;
};

export const LLMOptionToSavedPlaygroundModelState = (
  llmOption: LLMOption
): SavedPlaygroundModelState => {
  return {
    llmModelId: llmOption.baseModelId ?? null,
    objectId: llmOption.objectId ?? null,
    savedModelParams: llmOption.defaultParams ?? null,
    isLatest: llmOption.isLatest ?? false,
    versionIndex: llmOption.versionIndex ?? null,
  };
};
